import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import {
  addAdminService,
  updateAdminService,
  getByIdAdminService,
} from '@/services/AdminServices';
import { getRoleService } from '@/services/RoleServices';
import validationErrors from '@/lib/validationErrors';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import { useDispatch } from 'react-redux';
import ToastMe from '@/components/ui/ToastMe';

/* Options + helpers */
const dayOfWeekOptions = [
  { value: 0, label: 'Sunday' },  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },{ value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Same calculation as Employees: 22 workdays/month, 8 hours/day
const calcRate = (gross) => {
  const g = Number(gross) || 0;
  return {
    monthly: g,
    daily  : +(g / 22).toFixed(2),
    hourly : +(g / (22 * 8)).toFixed(2),
  };
};

// Resolve current branch id: LS → Axios header → JWT
function getEffectiveBranchId() {
  const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

  try {
    const ls = localStorage.getItem('branch_id');
    if (ls && ls !== 'all' && isObjectId(ls)) return ls;
  } catch {}

  try {
    const hdr = axios?.defaults?.headers?.common?.['X-Branch-Id'];
    if (hdr && hdr !== 'all' && isObjectId(hdr)) return hdr;
  } catch {}

  try {
    const tok =
      localStorage.getItem('customer_jwt') ||
      localStorage.getItem('customerToken') ||
      localStorage.getItem('jwt_token');
    if (tok) {
      const d = jwtDecode(tok) || {};
      const def = d?.branch_id;
      if (isObjectId(def)) return def;
    }
  } catch {}

  return null;
}

const AddEditDrawer = ({
  titleText,
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
  waiterRoleId, // ✅ passed from page
}) => {
  const selectRef = useRef(null);
  const dispatch  = useDispatch();

  const {
    setValue,
    getValues,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone_no: '',
      status: '1',
      password: '',
      confirm_password: '',
      role_id: '',
      salary: '',
      contractStartDate: '',
      contractEndDate: '',
      holidayDays: [],
      defaultInTime: '',
      defaultOutTime: '',
      penaltyRate: '',
      biometricId: '',
    },
  });

  const [rolesOptionList, setRolesOptionList] = useState([]);
  const [itemStatus, setItemStatus]           = useState('1');
  const [isLoading, setIsLoading]             = useState(false);
  const [isFormSubmit, setIsFormSubmit]       = useState(false);
  const [salaryRate, setSalaryRate]           = useState({ monthly: '', daily: '', hourly: '' });

  // stable instance id (avoid portal clashes)
  const [roleSelectInstanceId] = useState(
    () => `waiter-role-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    if (activeDrawer) {
      formReset();
      setSalaryRate({ monthly: '', daily: '', hourly: '' });
      fetchWaiterRoleOnly();
      if (editDataId) getDataById(editDataId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer]);

  const formReset = () => {
    reset();
    setValue('status', '1');
    setItemStatus('1');
  };

  const handleDrawerClose = () => {
    formReset();
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  const handleChangeStatus = (e) => {
    setValue('status', e.target.value);
    setItemStatus(e.target.value);
  };

  /* ✅ Only fetch the specific waiter role and inject it */
  const fetchWaiterRoleOnly = () => {
    if (!waiterRoleId) {
      ToastMe('Waiter role id missing. Check WAITERS_ROLE_ID.', 'error');
      return;
    }
    setIsLoading(true);
    dispatch(getRoleService({
      search: '',
      page: 1,
      limit: 10,
      extra_filter: { _id: waiterRoleId },
    }))
      .then((res) => {
        const rows = res?.data?.data?.data || [];
        const role = rows.find((r) => String(r?._id) === String(waiterRoleId));

        if (!role) {
          setRolesOptionList([]);
          setValue('role_id', '');
          ToastMe('Waiter role not found in roles list. Ensure seeding is correct.', 'error');
          return;
        }

        const opt = { value: role._id, label: role.name || 'Waiter' };
        setRolesOptionList([opt]);

        // prefill and lock
        setValue('role_id', opt.value, { shouldValidate: true });
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const getDataById = (editId) => {
    setIsLoading(true);
    dispatch(getByIdAdminService(editId))
      .then((res) => {
        const d = res?.data?.data;
        if (!d) return;

        setValue('name', d.name || '');
        setValue('email', d.email || '');
        setValue('phone_no', d.phone_no || '');
        setValue('status', String(d.status || 1));
        setItemStatus(String(d.status || 1));

        setValue('password', '');
        setValue('confirm_password', '');

        // ✅ Force waiter role even if record had something else
        setValue('role_id', waiterRoleId || d.role_id || '');

        setValue('salary', d.salary || '');
        setSalaryRate(
          d.salaryRate
            ? { monthly: d.salaryRate.monthly, daily: d.salaryRate.daily, hourly: d.salaryRate.hourly }
            : calcRate(d.salary)
        );

        setValue('contractStartDate', d.contractStartDate ? new Date(d.contractStartDate).toISOString().split('T')[0] : '');
        setValue('contractEndDate',   d.contractEndDate   ? new Date(d.contractEndDate).toISOString().split('T')[0] : '');
        setValue('holidayDays', Array.isArray(d.holidayDays) ? d.holidayDays : []);
        setValue('defaultInTime',  d.defaultInTime  || '');
        setValue('defaultOutTime', d.defaultOutTime || '');
        setValue('penaltyRate',    d.penaltyRate    || '');
        setValue('biometricId',    d.biometricId    || '');
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const onSubmit = async (formData) => {
    if (formData.confirm_password !== formData.password) {
      ToastMe('Password and confirm password do not match', 'error');
      return;
    }
    setIsFormSubmit(true);

    // ✅ Hard-set waiter role on submit
    if (waiterRoleId) formData.role_id = waiterRoleId;

    // ✅ Assign current branch to the user being created/updated
    const effectiveBranch = getEffectiveBranchId();
    if (effectiveBranch) {
      formData.branch_ids = [effectiveBranch];
      formData.default_branch_id = effectiveBranch;
      // Optional: for servers that expect also a flat branch_id
      formData.branch_id = effectiveBranch;
      console.log('🔗 Using branch for waiter:', effectiveBranch);
    } else {
      console.warn('❌ No effective branch found (LS/header/JWT).');
      ToastMe('Select a branch (top bar) before creating a waiter.', 'warning');
    }

    formData.isSuperAdmin      = 0;
    formData.contractStartDate = formData.contractStartDate || null;
    formData.contractEndDate   = formData.contractEndDate   || null;
    formData.holidayDays       = Array.isArray(formData.holidayDays) ? formData.holidayDays : [];

    const svc = editDataId ? updateAdminService(editDataId, formData) : addAdminService(formData);

    dispatch(svc)
      .then((res) => {
        if (!editDataId) {
          const biometricId = res?.data?.data?.biometricId;
          const branchInfo  = formData.branch_ids ? ` (branch: ${formData.default_branch_id})` : '';
          ToastMe(`Waiter created${branchInfo}. Biometric ID: ${biometricId ?? '—'}`, 'success');
        } else {
          ToastMe('Waiter updated successfully', 'success');
        }
        handleDrawerClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsFormSubmit(false));
  };

  const selectPortalProps = {
    menuPortalTarget: typeof window !== 'undefined' ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Name */}
          <Controller
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 20 }}
            name="name"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Name" type="text"
                     errorData={{ minLength: '2', maxLength: '20' }} errorType={errors?.name?.type}/>
            )}
          />

          {/* Email */}
          <Controller
            control={control}
            rules={{ required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ }}
            name="email"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Email" type="text"
                     errorData={{ minLength: '6', maxLength: '50' }} errorType={errors?.email?.type}/>
            )}
          />

          {/* Phone */}
          <Controller
            control={control}
            rules={{ required: true, minLength: 10, maxLength: 12 }}
            name="phone_no"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Phone" type="text"
                     errorData={{ minLength: '10', maxLength: '12' }} errorType={errors?.phone_no?.type}/>
            )}
          />

          {/* Status */}
          <div className="form-input-item">
            <label className="db-field-title required" htmlFor="active">Status</label>
            <div className="db-field-radio-group">
              <input type="radio" id="active" name="status" value="1" checked={itemStatus === '1'} onChange={handleChangeStatus}/>
              <label htmlFor="active">Active</label>
              <input type="radio" id="inactive" name="status" value="2" checked={itemStatus === '2'} onChange={handleChangeStatus}/>
              <label htmlFor="inactive">Inactive</label>
            </div>
          </div>

          {/* Password */}
          <Controller
            control={control}
            rules={editDataId ? { minLength: 6, maxLength: 20 } : { required: true, minLength: 6, maxLength: 20 }}
            name="password"
            render={({ field }) => (
              <Input {...field} labelStyleClassName={editDataId ? '' : 'db-field-title required'}
                     type="password" labelText="New Password" autoComplete="off"
                     errorData={{ minLength: '6', maxLength: '20' }} errorType={errors?.password?.type}/>
            )}
          />

          {/* Confirm */}
          <Controller
            control={control}
            rules={editDataId ? { minLength: 6, maxLength: 20 } : { required: true, minLength: 6, maxLength: 20 }}
            name="confirm_password"
            render={({ field }) => (
              <Input {...field} labelStyleClassName={editDataId ? '' : 'db-field-title required'}
                     type="password" labelText="Confirm Password" autoComplete="off"
                     errorData={{ minLength: '6', maxLength: '20' }} errorType={errors?.confirm_password?.type}/>
            )}
          />

          {/* Role (InputSelect look, disabled & locked) */}
          <Controller
            control={control}
            name="role_id"
            rules={{ required: true }}
            render={({ field }) => {
              const valueOption =
                rolesOptionList.find((o) => o.value === field.value)
                || rolesOptionList[0]
                || null;

              return (
                <>
                  <InputSelect
                    ref={selectRef}
                    id="role_id"
                    instanceId={roleSelectInstanceId}
                    labelText="Role"
                    options={rolesOptionList}
                    value={valueOption}
                    onChange={() => {/* locked: ignore changes */}}
                    isDisabled
                    isClearable={false}
                    isSearchable={false}
                    errorType={errors?.role_id?.type}
                    {...selectPortalProps}
                  />
                  <input type="hidden" value={waiterRoleId || field.value || ''} readOnly />
                </>
              );
            }}
          />

          {/* Salary */}
          <Controller
            control={control}
            name="salary"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(e) => { field.onChange(e); setSalaryRate(calcRate(e.target.value)); }}
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Salary"
                type="number"
                errorData={{ required: 'Salary is required' }}
                errorType={errors?.salary?.type}
              />
            )}
          />

          {/* Salary rate mirrors */}
          <Input readOnly value={salaryRate.monthly} labelStyleClassName="db-field-title" size="sm" labelText="Monthly (read-only)" type="number" />
          <Input readOnly value={salaryRate.daily}   labelStyleClassName="db-field-title" size="sm" labelText="Daily (read-only)"   type="number" />
          <Input readOnly value={salaryRate.hourly}  labelStyleClassName="db-field-title" size="sm" labelText="Hourly (read-only)"  type="number" />

          {/* Biometric ID */}
          <Controller
            control={control}
            name="biometricId"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Biometric ID" type="text" />
            )}
          />

          {/* Contract dates */}
          <Controller
            control={control}
            name="contractStartDate"
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Contract Start Date" type="date"
                     errorData={{ required: 'Contract start date is required' }} errorType={errors?.contractStartDate?.type}/>
            )}
          />
          <Controller
            control={control}
            name="contractEndDate"
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Contract End Date" type="date"
                     errorData={{ required: 'Contract end date is required' }} errorType={errors?.contractEndDate?.type}/>
            )}
          />

          {/* Holidays */}
          <Controller
            control={control}
            name="holidayDays"
            render={({ field }) => {
              const val = (field.value || []).map(
                (n) => dayOfWeekOptions.find((o) => o.value === n) || { value: n, label: `Day ${n}` }
              );
              return (
                <Select
                  isMulti
                  placeholder="Select Holiday Days"
                  value={val}
                  onChange={(opts) => field.onChange(opts ? opts.map((o) => o.value) : [])}
                  options={dayOfWeekOptions}
                  styles={selectPortalProps.styles}
                  menuPortalTarget={selectPortalProps.menuPortalTarget}
                />
              );
            }}
          />

          {/* Default times */}
          <Controller control={control} name="defaultInTime"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Default In Time (HH:mm)" type="time" />
            )}
          />
          <Controller control={control} name="defaultOutTime"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Default Out Time (HH:mm)" type="time" />
            )}
          />

          {/* Penalty rate */}
          <Controller control={control} name="penaltyRate"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Penalty Rate (%)" type="number" />
            )}
          />
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="submit" className="db-btn py-2 text-white bg-primary" disabled={isFormSubmit || isLoading}>
              <i className="lab lab-save" /><span>Save</span>
              {isFormSubmit && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
            </button>
            <button type="button" className="modal-btn-outline modal-close" onClick={handleDrawerClose}>
              <i className="lab lab-close" /><span>Close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
};

export default AddEditDrawer;
